import { BaseModel } from '../base.model';

/**
 * Change signals for any subsystem, keyed by the domain that owns the data.
 *
 * Same document shape as the reporting signals that came before it — a counter a
 * client subscribes to, never the data itself — but the path carries a domain, so
 * authorization does not have to file itself under `reporting` to get live updates.
 *
 * The client re-reads through the API when `version` moves; nothing here is
 * authoritative, which is what keeps API permission checks the only gate on the
 * underlying data.
 */
export const SIGNALS_ROOT_COLLECTION = 'signals';

/**
 * Fixed segment between the domain and the entity id. Firestore alternates
 * collection/document, so without it `signals/{domain}/{entity}` would name a
 * collection rather than the document the watcher reads.
 */
export const SIGNAL_ENTITIES_COLLECTION = 'entities';

export enum SignalDomain {
  reporting = 'reporting',
  identity = 'identity',
  /** The Firestore projections in the `platform` schema: agents, their contact tables, agencies. */
  platform = 'platform',
}

export enum DomainSignalId {
  leadersBulletins = 'leaders-bulletins',
  userRoles = 'user-roles',
  /**
   * One id per projected table rather than one for the whole agent domain.
   *
   * A surface that renders only names has no reason to re-read because somebody's
   * phone number changed, and the split costs nothing on the publishing side: the
   * signal id is a trigger argument, so each table already emits its own.
   */
  agents = 'agents',
  agentEmailAddresses = 'agent-email-addresses',
  agentAddresses = 'agent-addresses',
  agentPhoneNumbers = 'agent-phone-numbers',
  agentApproveDenyReasons = 'agent-approve-deny-reasons',
  agencies = 'agencies',
  /** `reporting.reconciliation_by_carrier` was rebuilt by the CloudRun reconciliation worker. */
  reconciliation = 'reconciliation',
}

/**
 * One place that says which domain owns a signal. The outbox carries a single id
 * column, so the publisher resolves the domain from here rather than from a second
 * trigger argument — adding a signal stays a one-line `create trigger`.
 */
export const SIGNAL_DOMAIN_BY_ID: Readonly<Record<DomainSignalId, SignalDomain>> = {
  [DomainSignalId.leadersBulletins]: SignalDomain.reporting,
  [DomainSignalId.userRoles]: SignalDomain.identity,
  [DomainSignalId.agents]: SignalDomain.platform,
  [DomainSignalId.agentEmailAddresses]: SignalDomain.platform,
  [DomainSignalId.agentAddresses]: SignalDomain.platform,
  [DomainSignalId.agentPhoneNumbers]: SignalDomain.platform,
  [DomainSignalId.agentApproveDenyReasons]: SignalDomain.platform,
  [DomainSignalId.agencies]: SignalDomain.platform,
  [DomainSignalId.reconciliation]: SignalDomain.reporting,
};

/**
 * The agent projection, as the set a consumer of agent data watches.
 *
 * Anything reading an agent through the SQL backend is reading `platform.agents` plus
 * its child tables, so a consumer that watched only `agents` would miss an email or
 * address edit entirely.
 */
export const AGENT_PROJECTION_SIGNAL_IDS: readonly DomainSignalId[] = [
  DomainSignalId.agents,
  DomainSignalId.agentEmailAddresses,
  DomainSignalId.agentAddresses,
  DomainSignalId.agentPhoneNumbers,
  DomainSignalId.agentApproveDenyReasons,
];

export const DOMAIN_SIGNAL_IDS: DomainSignalId[] = Object.values(DomainSignalId);

const DOMAIN_SIGNAL_ID_SET = new Set<string>(DOMAIN_SIGNAL_IDS);

export function isDomainSignalId(value: string): value is DomainSignalId {
  return DOMAIN_SIGNAL_ID_SET.has(value);
}

export function buildDomainSignalPath(signalId: DomainSignalId): string {
  return [SIGNALS_ROOT_COLLECTION, SIGNAL_DOMAIN_BY_ID[signalId], SIGNAL_ENTITIES_COLLECTION, signalId].join('/');
}

export enum DomainSignalKeys {
  domain = 'domain',
  entity = 'entity',
  version = 'version',
  updatedAt = 'updatedAt',
  lastOperation = 'lastOperation',
  lastOutboxEventId = 'lastOutboxEventId',
  source = 'source',
  changedRowKeys = 'changedRowKeys',
}

export interface DomainSignal extends BaseModel {
  [DomainSignalKeys.domain]: SignalDomain;
  [DomainSignalKeys.entity]: DomainSignalId;
  /** Monotonic. Compare it; the value itself carries no meaning. */
  [DomainSignalKeys.version]: number;
  [DomainSignalKeys.updatedAt]: unknown;
  [DomainSignalKeys.lastOperation]: string;
  [DomainSignalKeys.lastOutboxEventId]: number;
  [DomainSignalKeys.source]: string;
  /**
   * The rows this publish covered — one entry per outbox event, deduplicated.
   *
   * Overwritten on every publish rather than appended, so it describes the latest
   * publish alone and never grows. Empty for a signal whose trigger records no key.
   */
  [DomainSignalKeys.changedRowKeys]?: string[];
}

export interface DomainSignalChange {
  version: number;
  /** Meaningful only when `isCoarse` is false. */
  changedRowKeys: string[];
  /**
   * True when the keys cannot account for the whole version jump, so the reader must
   * assume anything may have changed.
   */
  isCoarse: boolean;
}

/**
 * How much a version jump actually tells the reader.
 *
 * The contract that makes this work is on the publishing side: a publish that names its
 * rows advances `version` by exactly the number of rows it names. So the two agree if and
 * only if the reader saw every publish since `previousVersion` — and disagree whenever it
 * missed one, because those unseen publishes moved the version without leaving their keys
 * behind. A publish that cannot name its rows (no key column, or an event whose key was
 * null) advances the version by its event count and names nothing, which lands in the same
 * disagreement.
 *
 * Disagreement therefore means the keys are a subset of what moved — an offline client, or
 * a seed that emitted more events than one batch can carry. Treating a subset as the whole
 * story would leave stale rows on screen, so it degrades to a coarse refresh: exactly the
 * behaviour the Elasticsearch backend had for every change.
 *
 * The publisher and every reader have to agree on this rule, which is why it lives next to
 * the document shape rather than in any one consumer.
 */
export function resolveDomainSignalChange(args: {
  previousVersion: number;
  currentVersion: number;
  changedRowKeys?: readonly string[] | null;
}): DomainSignalChange {
  const changedRowKeys = (args.changedRowKeys ?? []).filter(
    (key): key is string => typeof key === 'string' && key.length > 0,
  );
  const delta = args.currentVersion - args.previousVersion;

  return {
    version: args.currentVersion,
    changedRowKeys,
    // A backwards or unchanged version is not a refresh cue at all, but if a caller asks
    // about one it is safer to answer "assume everything" than "nothing changed".
    isCoarse: delta <= 0 || !changedRowKeys.length || delta !== changedRowKeys.length,
  };
}
